import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_PHOTOBOOTH_EFFECT_ID,
  ENABLED_PHOTOBOOTH_EFFECTS,
  type PhotoboothEffectId,
  renderPhotoboothFrame,
} from '../photoboothEffects';
import { isUploadServerConfigured, uploadPhoto } from '../uploadApi';

type FacingMode = 'user' | 'environment';

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

function getCameraErrorMessage(err: unknown): string {
  const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Accès à la caméra refusé. Autorisez la caméra dans les paramètres du navigateur ou du site, puis réessayez.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'Aucune caméra détectée sur cet appareil.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'La caméra est peut-être utilisée par une autre application. Fermez les autres apps utilisant la caméra.';
  }
  if (name === 'OverconstrainedError') {
    return 'Les réglages demandés ne sont pas supportés par cette caméra. Essayez d’inverser la caméra.';
  }
  return 'Impossible d’accéder à la caméra. Vérifiez que vous êtes en HTTPS (ou sur localhost) et réessayez.';
}

export function PhotoboothPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const captureBlobRef = useRef<Blob | null>(null);
  const [facingMode, setFacingMode] = useState<FacingMode>('user');
  const [effectId, setEffectId] = useState<PhotoboothEffectId>(DEFAULT_PHOTOBOOTH_EFFECT_ID);
  const [error, setError] = useState<string | null>(null);
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [hasCaptureBlob, setHasCaptureBlob] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [frameSize, setFrameSize] = useState<{ width: number; height: number } | null>(null);

  const syncFrameSize = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;
    setFrameSize({ width, height });
  }, []);

  const uploadConfigured = isUploadServerConfigured();
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(
        'Votre navigateur ne permet pas d’utiliser la caméra depuis cette page. Essayez Chrome, Safari ou Firefox récents, en HTTPS.'
      );
      return;
    }

    let cancelled = false;
    let stream: MediaStream | null = null;
    let videoAttached: HTMLVideoElement | null = null;

    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        setError(null);
        setCapturedUrl(null);
        const video = videoRef.current;
        if (!video) {
          s.getTracks().forEach((t) => t.stop());
          stream = null;
          return;
        }
        videoAttached = video;
        video.srcObject = s;
        video.onloadedmetadata = () => {
          video.play().catch(() => {});
          syncFrameSize();
          if (!cancelled) setIsReady(true);
        };
        video.onresize = () => {
          if (!cancelled) syncFrameSize();
        };
      } catch (e) {
        if (!cancelled) {
          setError(getCameraErrorMessage(e));
          setIsReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      setIsReady(false);
      setFrameSize(null);
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      if (videoAttached) {
        videoAttached.onloadedmetadata = null;
        videoAttached.onresize = null;
        videoAttached.srcObject = null;
      }
    };
  }, [facingMode, syncFrameSize]);

  useEffect(() => {
    if (!isReady || capturedUrl) return undefined;

    let frameId = 0;
    const tick = (time: number) => {
      const video = videoRef.current;
      const canvas = previewCanvasRef.current;
      if (video && canvas && video.readyState >= 2) {
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (width && height) {
          if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
            syncFrameSize();
          }
          const ctx = canvas.getContext('2d');
          if (ctx) {
            renderPhotoboothFrame(ctx, video, width, height, {
              mirror: facingMode === 'user',
              effectId,
              time: time / 1000,
            });
          }
        }
      }
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [capturedUrl, effectId, facingMode, isReady, syncFrameSize]);

  const toggleCamera = useCallback(() => {
    setFacingMode((f) => (f === 'user' ? 'environment' : 'user'));
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = previewCanvasRef.current;
    if (!video || !canvas || video.readyState < 2) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    renderPhotoboothFrame(ctx, video, w, h, {
      mirror: facingMode === 'user',
      effectId,
      time: performance.now() / 1000,
    });

    const url = canvas.toDataURL('image/jpeg', 0.96);
    setCapturedUrl(url);
    setFrameSize({ width: w, height: h });
    setUploadStatus('idle');
    setUploadErrorMessage(null);
    setHasCaptureBlob(false);
    captureBlobRef.current = null;
    canvas.toBlob(
      (blob) => {
        if (blob) {
          captureBlobRef.current = blob;
          setHasCaptureBlob(true);
        }
      },
      'image/jpeg',
      0.96
    );
  }, [effectId, facingMode]);

  const downloadPhoto = useCallback(() => {
    if (!capturedUrl) return;
    const a = document.createElement('a');
    a.href = capturedUrl;
    a.download = `souvenir-mariage-${Date.now()}.jpg`;
    a.rel = 'noopener';
    a.click();
  }, [capturedUrl]);

  const retake = useCallback(() => {
    setCapturedUrl(null);
    setHasCaptureBlob(false);
    captureBlobRef.current = null;
    setUploadStatus('idle');
    setUploadErrorMessage(null);
    requestAnimationFrame(() => {
      const video = videoRef.current;
      if (video?.srcObject) {
        video.play().catch(() => {});
      }
    });
  }, []);

  const sendToServer = useCallback(async () => {
    const blob = captureBlobRef.current;
    if (!blob || !uploadConfigured) return;
    setUploadStatus('uploading');
    setUploadErrorMessage(null);
    try {
      await uploadPhoto(blob, `capture-${Date.now()}.jpg`);
      setUploadStatus('success');
    } catch (e) {
      setUploadStatus('error');
      setUploadErrorMessage(
        "Oops, erreur lors de l'envoi de la photo : " + (e instanceof Error ? e.message : '')
      );
      console.error(e);
    }
  }, [uploadConfigured]);

  const showServerUpload = uploadConfigured && uploadStatus !== 'success';
  const effectColumnSplit = Math.ceil(ENABLED_PHOTOBOOTH_EFFECTS.length / 2);
  const leftEffects = ENABLED_PHOTOBOOTH_EFFECTS.slice(0, effectColumnSplit);
  const rightEffects = ENABLED_PHOTOBOOTH_EFFECTS.slice(effectColumnSplit);

  const frameStyle = frameSize
    ? { aspectRatio: `${frameSize.width} / ${frameSize.height}` }
    : undefined;

  return (
    <main className={`photobooth${capturedUrl ? ' photobooth--captured' : ''}`}>
      <div className="photobooth__inner">
        <header className="photobooth__header">
          <h1 className="photobooth__title">Photobooth</h1>
          <p className="photobooth__intro">
            Rendez-vous au coin photo, prenez un cliché avec votre appareil et si celui-ci vous convient,
            validez-le pour nous laisser un souvenir !
          </p>
        </header>

        {error && (
          <div className="photobooth__alert" role="alert">
            {error}
          </div>
        )}

        <div
          className={`photobooth__workspace${capturedUrl ? ' photobooth__workspace--captured' : ''}`}
          role={capturedUrl ? undefined : 'radiogroup'}
          aria-label={capturedUrl ? undefined : 'Effets et masques'}
        >
          {!capturedUrl && (
            <div className="photobooth__effects photobooth__effects--side photobooth__effects--left">
              {leftEffects.map((effect) => (
                <button
                  key={effect.id}
                  type="button"
                  role="radio"
                  aria-checked={effectId === effect.id}
                  className={`photobooth__effect${effectId === effect.id ? ' photobooth__effect--active' : ''}`}
                  onClick={() => setEffectId(effect.id)}
                >
                  <span className="photobooth__effect-emoji" aria-hidden>
                    {effect.emoji}
                  </span>
                  <span className="photobooth__effect-label">{effect.label}</span>
                </button>
              ))}
            </div>
          )}

          <div className="photobooth__stage">
            <div
              className="photobooth__frame photobooth__video-wrap"
              style={frameStyle}
              hidden={!!capturedUrl}
              aria-hidden={!!capturedUrl}
            >
              <video
                ref={videoRef}
                className="photobooth__video photobooth__video--source"
                playsInline
                muted
                autoPlay
                aria-hidden
                tabIndex={-1}
              />
              <canvas
                ref={previewCanvasRef}
                className="photobooth__preview-canvas"
                aria-label="Aperçu caméra en direct"
              />
              {!isReady && !error && <p className="photobooth__loading">Connexion à la caméra…</p>}
            </div>
            {capturedUrl && (
              <div className="photobooth__frame photobooth__preview-wrap" style={frameStyle}>
                <img
                  src={capturedUrl}
                  alt="Portrait enregistré"
                  className="photobooth__preview"
                  width={frameSize?.width}
                  height={frameSize?.height}
                />
              </div>
            )}
          </div>

          {!capturedUrl && (
            <div className="photobooth__effects photobooth__effects--side photobooth__effects--right">
              {rightEffects.map((effect) => (
                <button
                  key={effect.id}
                  type="button"
                  role="radio"
                  aria-checked={effectId === effect.id}
                  className={`photobooth__effect${effectId === effect.id ? ' photobooth__effect--active' : ''}`}
                  onClick={() => setEffectId(effect.id)}
                >
                  <span className="photobooth__effect-emoji" aria-hidden>
                    {effect.emoji}
                  </span>
                  <span className="photobooth__effect-label">{effect.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {uploadStatus === 'success' && (
          <p className="photobooth__success" role="status">
            Merci pour ce magnifique souvenir 💚 !
          </p>
        )}

        {uploadStatus === 'error' && uploadErrorMessage && (
          <div className="photobooth__alert" role="alert">
            {uploadErrorMessage}
          </div>
        )}

        <div className="photobooth__actions">
          {!capturedUrl ? (
            <>
              <button
                type="button"
                className="photobooth__btn photobooth__btn--primary"
                onClick={capture}
                disabled={!isReady || !!error}
              >
                Prendre la photo
              </button>
              <button
                type="button"
                className="photobooth__btn photobooth__btn--secondary"
                onClick={toggleCamera}
                disabled={!!error}
              >
                Inverser la caméra
              </button>
            </>
          ) : (
            <>
              {showServerUpload && (
                <button
                  type="button"
                  className="photobooth__btn photobooth__btn--primary"
                  onClick={sendToServer}
                  disabled={uploadStatus === 'uploading' || !hasCaptureBlob}
                >
                  {uploadStatus === 'uploading' ? 'Envoi en cours…' : 'Valider la photo'}
                </button>
              )}
              <button
                type="button"
                className={`photobooth__btn photobooth__btn--secondary${showServerUpload ? '' : ' photobooth__btn--primary'}`}
                onClick={downloadPhoto}
                disabled={uploadStatus === 'uploading'}
              >
                Télécharger une copie
              </button>
              <button type="button" className="photobooth__btn photobooth__btn--secondary" onClick={retake}>
                Reprendre une photo
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
